"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  format,
  addDays,
  startOfDay,
  isBefore,
  startOfToday,
  addMinutes,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useCallback, useEffect, useRef } from "react";
import { CartItem } from "@/types/service";
import { ServiceStaff } from "./service-staff";
import { ServiceSlotsLoader } from "./service-slots-loader";
import { Availability } from "../../types/availability";
import { ApiEndpoint, apiService } from "../../lib/api";
import { Slots, TimeSlot } from "../../types/slots";
import { User } from "../../types/user";
import { browserTimezone } from "../../helper/utility";
import { formatInTimeZone } from "date-fns-tz";

interface ServiceSlotsProps {
  selectedServices: CartItem[];
  resource: any;
  onCartUpdate: (items: CartItem[]) => void;
}

interface ServiceAvailability {
  [key: string]: {
    loading: boolean;
    data: Availability | null;
    error: string | null;
  };
}

export function ServiceSlots({
  selectedServices,
  resource,
  onCartUpdate,
}: ServiceSlotsProps) {
  const today = new Date();
  const [startDate, setStartDate] = useState(startOfDay(today));
  const [serviceAvailabilityData, setServiceAvailabilityData] =
    useState<ServiceAvailability>({});
  const [use24Hour, setUse24Hour] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Record<string, Date>>({});

  // Track if auto-selection has already been performed for each service
  const autoSelectedRef = useRef<Record<string, boolean>>({});

  const autoSelectFirstDate = () => {
    const dateKey = format(startDate, "yyyy-MM-dd");
    const updatedSelectedDates: Record<string, Date> = {};
    const updatedServices: CartItem[] = [];
    const usedSlots = new Set<string>(); // Set to track used slot times (e.g., "2024-05-01T10:00")

    selectedServices.forEach((item) => {
      const serviceId = item.service.id.toString();
      const autoSelectKey = `${serviceId}-${dateKey}`;
      const availability = serviceAvailabilityData[serviceId]?.data;
      if (availability) {
        const { slots, duration, time_zone } = availability;

        // Find first available slot + time not already used
        let matchedSlot: {
          date: string;
          timeSlot: TimeSlot;
        } | null = null;

        for (const slot of slots) {
          const slotDate = new Date(slot.date);
          if (
            isBefore(slotDate, today) ||
            !slot.hours.some((h) => h.available)
          ) {
            continue;
          }

          for (const hour of slot.hours) {
            const fullTimeKey = `${slot.date}T${hour.time}`;
            if (hour.available && !usedSlots.has(fullTimeKey)) {
              matchedSlot = {
                date: slot.date,
                timeSlot: hour,
              };
              usedSlots.add(fullTimeKey); // Mark as used
              break;
            }
          }

          if (matchedSlot) break;
        }

        if (matchedSlot) {
          const { date, timeSlot } = matchedSlot;
          updatedSelectedDates[serviceId] = new Date(date);
          updatedServices.push({
            ...item,
            slot: {
              start_at: timeSlot.time,
              end_at: formatInTimeZone(
                addMinutes(new Date(timeSlot.time), duration!),
                time_zone,
                "yyyy-MM-dd'T'HH:mm:ssxxx"
              ),
              available: timeSlot.available,
              duration,
              time_zone,
            },
          });
        }
      }
      autoSelectedRef.current[autoSelectKey] = true;
    });

    if (Object.keys(updatedSelectedDates).length > 0) {
      setSelectedDates((prev) => ({
        ...prev,
        ...updatedSelectedDates,
      }));
    }

    if (updatedServices.length > 0) {
      const newCart = selectedServices.map((s) => {
        const updated = updatedServices.find(
          (u) => u.service.id === s.service.id
        );
        return updated ?? s;
      });
      onCartUpdate(newCart);
    }
  };

  const getAvailability = useCallback(
    async (serviceId: string, duration: number, date: Date) => {
      setServiceAvailabilityData((prev) => ({
        ...prev,
        [serviceId]: {
          ...prev[serviceId],
          loading: true,
          error: null,
        },
      }));

      try {
        const startDate = format(date, "yyyy-MM-dd");
        const endDate = format(addDays(date, 7), "yyyy-MM-dd");

        const response = await apiService.list(
          `${ApiEndpoint.availability}/${resource.resource_id}`,
          {
            start: startDate,
            end: endDate,
            duration: duration,
            service_id: serviceId,
            time_zone: browserTimezone,
          }
        );
        setServiceAvailabilityData((prev) => ({
          ...prev,
          [serviceId]: {
            loading: false,
            data: response,
            error: null,
          },
        }));
      } catch (error) {
        setServiceAvailabilityData((prev) => ({
          ...prev,
          [serviceId]: {
            loading: false,
            data: null,
            error: "Failed to fetch availability",
          },
        }));
      }
    },
    [resource.resource_id]
  );

  useEffect(() => {
    const dateKey = format(startDate, "yyyy-MM-dd");
    selectedServices.forEach((item) => {
      const serviceId = item.service.id.toString();
      const autoSelectKey = `${serviceId}-${dateKey}`;
      // Only fetch if we haven't auto-selected for this service and date
      if (!autoSelectedRef.current[autoSelectKey]) {
        getAvailability(serviceId, item.price.duration, startDate);
      }
    });
  }, [selectedServices, getAvailability, startDate]);

  useEffect(() => {
    const selectedServiceIds = selectedServices.map((s) =>
      s.service.id.toString()
    );

    // Check if all selected services have availability data loaded (and no loading or error)
    const allLoaded = selectedServiceIds.every((id) => {
      return serviceAvailabilityData[id]?.data;
    });

    if (allLoaded) {
      autoSelectFirstDate();
    }
  }, [serviceAvailabilityData]);

  const handleStaffSelect = (
    serviceId: string,
    staffId: string,
    staffName: string
  ) => {
    const updatedServices = selectedServices.map((service) =>
      service.service.id.toString() === serviceId
        ? {
            ...service,
            staff: staffId ? { id: staffId, name: staffName } : undefined,
          }
        : service
    );
    onCartUpdate(updatedServices);
  };

  const handleDateSelect = (serviceId: string, date: Date) => {
    setSelectedDates((prev) => ({ ...prev, [serviceId]: date }));
    const updatedServices = selectedServices.map((service) =>
      service.service.id.toString() === serviceId
        ? { ...service, slot: undefined }
        : service
    );
    onCartUpdate(updatedServices);
  };

  const handleTimeSelect = (serviceId: string, timeSlot: TimeSlot) => {
    const { time_zone, duration } = serviceAvailabilityData[serviceId].data!;

    const updatedServices = selectedServices.map((service) =>
      service.service.id.toString() === serviceId
        ? {
            ...service,
            slot: {
              start_at: timeSlot.time,
              end_at: formatInTimeZone(
                addMinutes(new Date(timeSlot.time), duration!),
                time_zone,
                "yyyy-MM-dd'T'HH:mm:ssxxx"
              ),
              available: timeSlot.available,
              duration: duration,
              time_zone: time_zone,
            },
          }
        : service
    );
    onCartUpdate(updatedServices);
  };

  const handlePrevWeek = () => {
    autoSelectedRef.current = {};
    setStartDate((prev) => addDays(prev, -7));
  };

  const handleNextWeek = () => {
    autoSelectedRef.current = {};
    setStartDate((prev) => addDays(prev, 7));
  };

  const handleToday = () => {
    autoSelectedRef.current = {};
    setStartDate(startOfToday());
  };

  const isDateDisabled = (date: Date, slots: Slots[]) => {
    if (isBefore(date, startOfToday())) return true;

    const dateStr = format(date, "yyyy-MM-dd");
    const dateSlot = slots.find((slot) => slot.date === dateStr);
    return !dateSlot || !dateSlot.hours.some((h) => h.available);
  };

  const isTimeSlotDisabled = (
    timeSlot: TimeSlot,
    slots: Slots[],
    date: Date,
    staffId: string
  ) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const dateSlot = slots.find((slot) => slot.date === dateStr);
    if (!dateSlot) return true;

    if (!timeSlot || !timeSlot.available) return true;

    // If staff is selected, check if they're available for this slot
    if (staffId && !timeSlot.users.includes(Number(staffId))) {
      return true;
    }

    return false;
  };

  const getAvailableSlots = (slots: Slots[], date: Date, staffId: string) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const dateSlot = slots.find((slot) => slot.date === dateStr);
    if (!dateSlot) return [];

    return dateSlot.hours.filter((hour) => {
      if (!hour.available) return false;
      if (staffId && !hour.users.includes(Number(staffId))) return false;
      return true;
    });
  };

  return (
    <div className="space-y-6">
      {selectedServices.map((item) => {
        const serviceId = item.service.id.toString();
        const availability = serviceAvailabilityData[serviceId];
        const isLoading = !availability || availability.loading;

        if (isLoading) {
          return <ServiceSlotsLoader key={serviceId} />;
        }

        if (availability.error || !availability.data) {
          return (
            <Card key={serviceId} className="bg-destructive/5">
              <CardHeader>
                <CardTitle className="text-lg">{item.service.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-destructive">{availability.error}</p>
              </CardContent>
            </Card>
          );
        }

        const { slots, users, duration } = availability.data;
        const staffId = item.staff?.id;
        const availableSlots = selectedDates[serviceId]
          ? getAvailableSlots(slots, selectedDates[serviceId], staffId || "")
          : [];

        return (
          <Card key={serviceId}>
            <CardHeader>
              <CardTitle className="text-lg">{item.service.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <ServiceStaff
                serviceId={serviceId}
                users={users}
                selectedStaff={item.staff?.id || ""}
                staffSearch={""}
                onStaffSelect={(serviceId, staffId) => {
                  const staff = users.find(
                    (u: User) => u.user_id.toString() === staffId
                  );
                  handleStaffSelect(serviceId, staffId, staff?.name || "");
                }}
                onSearchChange={() => {}}
                getFirstAvailable={(userId) => {
                  for (const slot of slots) {
                    for (const hour of slot.hours) {
                      if (
                        hour.available &&
                        (!userId || hour.users.includes(Number(userId)))
                      ) {
                        return format(
                          new Date(hour.time),
                          use24Hour ? "HH:mm" : "h:mm aa"
                        );
                      }
                    }
                  }
                  return null;
                }}
              />

              {/* Date Selection */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-medium">Select Date</label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handlePrevWeek}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleToday}>
                      Today
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleNextWeek}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                  {Array.from({ length: 7 }, (_, i) =>
                    addDays(startDate, i)
                  ).map((date) => (
                    <Button
                      key={date.toISOString()}
                      variant={
                        selectedDates[serviceId]?.toDateString() ===
                        date.toDateString()
                          ? "default"
                          : "outline"
                      }
                      className="flex-col h-auto py-3"
                      onClick={() => handleDateSelect(serviceId, date)}
                      disabled={isDateDisabled(date, slots)}
                    >
                      <div className="font-medium">{format(date, "EEE")}</div>
                      <div className="text-sm">{format(date, "MMM d")}</div>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-medium">Available Times</label>
                  <div className="inline-flex rounded-md border border-input overflow-hidden">
                    <Button
                      variant={!use24Hour ? "secondary" : "ghost"}
                      size="sm"
                      className="h-8 rounded-none border-0"
                      onClick={() => setUse24Hour(false)}
                    >
                      12h
                    </Button>
                    <div className="w-px bg-border h-8" />
                    <Button
                      variant={use24Hour ? "secondary" : "ghost"}
                      size="sm"
                      className="h-8 rounded-none border-0"
                      onClick={() => setUse24Hour(true)}
                    >
                      24h
                    </Button>
                  </div>
                </div>
                {availableSlots.length > 0 ? (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {availableSlots.map((slot) => {
                      return (
                        <Button
                          key={slot.time}
                          variant={
                            item.slot?.start_at === slot.time
                              ? "default"
                              : "outline"
                          }
                          className="w-full"
                          disabled={isTimeSlotDisabled(
                            slot,
                            slots,
                            selectedDates[serviceId],
                            staffId || ""
                          )}
                          onClick={() => handleTimeSelect(serviceId, slot)}
                        >
                          {format(
                            new Date(slot.time),
                            use24Hour ? "HH:mm" : "h:mm a"
                          )}
                        </Button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No slots available for the selected date
                    {staffId ? " and staff member" : ""}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
